import React from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardServices from "../../../service/dashboard";
import { usePerms } from "../../../hooks/usePerms";

/* -------------------------------------------------------------------------- */
/*  Design tokens                                                             */
/* -------------------------------------------------------------------------- */
const COLORS = {
  primary: "#0F4C75",
  primaryLight: "#3282B8",
  accent: "#1B9C85",
  success: "#2E7D32",
  warning: "#ED6C02",
  error: "#D32F2F",
  surface: "#FFFFFF",
  background: "#F4F7FA",
  border: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  muted: "#94A3B8",
};

const CHART_COLORS = ["#0F4C75", "#1B9C85", "#ED6C02", "#D32F2F", "#64748B"];

const PERIODS = [
  "Today",
  "Yesterday",
  "This Week",
  "This Month",
  "This Year",
  "Custom Date",
];

/* -------------------------------------------------------------------------- */
/*  Reusable UI components                                                    */
/* -------------------------------------------------------------------------- */
const SectionCard = ({ title, action, children, sx }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      height: "100%",
      backgroundColor: COLORS.surface,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
      transition: "box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
      },
      ...sx,
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, md: 2.75 },
        "&:last-child": { pb: { xs: 2, md: 2.75 } },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2.25}
      >
        <Typography
          variant="subtitle1"
          fontWeight={650}
          sx={{ color: COLORS.textPrimary, letterSpacing: "-0.01em" }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const MetricCard = ({
  label,
  value,
  hint,
  color = COLORS.primary,
  icon: Icon,
  onClick,
}) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      backgroundColor: COLORS.surface,
      transition: "all 0.2s ease",
      "&:hover": onClick
        ? {
            borderColor: alpha(color, 0.4),
            boxShadow: `0 6px 16px ${alpha(color, 0.12)}`,
            transform: "translateY(-1px)",
          }
        : {},
    }}
  >
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ color: COLORS.textSecondary, fontWeight: 500, mb: 0.75 }}
          >
            {label}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color,
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              sx={{ color: COLORS.muted, mt: 0.75, display: "block" }}
            >
              {hint}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              backgroundColor: alpha(color, 0.1),
              color,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const EmptyState = ({ children }) => (
  <Box
    sx={{
      minHeight: 140,
      display: "grid",
      placeItems: "center",
      textAlign: "center",
      color: COLORS.textSecondary,
      border: `1px dashed ${COLORS.border}`,
      borderRadius: 2,
      px: 3,
      py: 3,
      backgroundColor: alpha(COLORS.background, 0.6),
    }}
  >
    <Typography variant="body2" sx={{ maxWidth: 280 }}>
      {children}
    </Typography>
  </Box>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <Box sx={{ mt: 4, mb: 2 }}>
    <Typography
      variant="overline"
      sx={{
        color: COLORS.primaryLight,
        fontWeight: 700,
        letterSpacing: "0.1em",
        fontSize: "0.7rem",
      }}
    >
      {eyebrow}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{
        color: COLORS.textPrimary,
        letterSpacing: "-0.02em",
        lineHeight: 1.25,
        mt: 0.25,
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        variant="body2"
        sx={{ color: COLORS.textSecondary, mt: 0.5, maxWidth: 560 }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

const StatusChip = ({ label, status }) => {
  const map = {
    Overdue: { color: "error", bg: alpha(COLORS.error, 0.1) },
    "Due soon": { color: "warning", bg: alpha(COLORS.warning, 0.12) },
    "On track": { color: "success", bg: alpha(COLORS.success, 0.1) },
  };
  const config = map[status] || { color: "default", bg: "transparent" };

  return (
    <Chip
      size="small"
      label={label || status}
      color={config.color}
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        height: 24,
        backgroundColor: config.bg,
      }}
    />
  );
};

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */
const DashboardOverview = () => {
  const { role, user, department } = usePerms("", ["user"]);
  const navigate = useNavigate();
  const [period, setPeriod] = React.useState("This Month");
  const [customStart, setCustomStart] = React.useState("");
  const [customEnd, setCustomEnd] = React.useState("");
  const isCustomRangeReady =
    period !== "Custom Date" ||
    (Boolean(customStart) && Boolean(customEnd) && customStart <= customEnd);

  const dashboardQuery = useQuery({
    queryKey: [
      "dashboard-overview",
      role,
      user,
      department,
      period,
      customStart,
      customEnd,
    ],
    queryFn: () =>
      DashboardServices.getDashboardOverview({
        role,
        user,
        department,
        period,
        customStart,
        customEnd,
      }),
    enabled:
      role !== null &&
      role !== undefined &&
      (!role || Boolean(user)) &&
      isCustomRangeReady,
    // Keep showing the previous period's data while the next one loads
    // instead of flashing back to the full-page skeleton on every change.
    placeholderData: keepPreviousData,
  });

  const liveData = dashboardQuery.data;
  const hasLiveData = Boolean(
    liveData &&
      ((liveData.totals?.assets ?? 0) > 0 ||
        (liveData.totals?.inspections ?? 0) > 0 ||
        (liveData.totals?.issues ?? 0) > 0),
  );
  const overview = liveData || {};

  const {
    totalAssets = 0,
    periodInspections = 0,
    completed = 0,
    completionRate = 0,
    affectedAssets = 0,
    periodIssues = 0,
  } = overview.summary || {};
  const {
    categoryA = 0,
    categoryB = 0,
    categoryC = 0,
    openIssues = 0,
  } = overview.issueCategories || {};
  const serviceDueCount = overview.serviceDue?.count ?? 0;
  const {
    totalUsers = 0,
    activeUsers = 0,
    inspectionOperators = 0,
  } = overview.staffActivity || {};
  const intervalReport = overview.intervalReport || [];
  const intervalAlerts = intervalReport.filter((item) => item.status !== "On track");
  const assetsWithoutPrestart = overview.assetsWithoutPrestart || [];
  const assetTypes = overview.fleetComposition || [];
  const assetStatuses = overview.assetStatuses || [];
  const departments = overview.departmentsBreakdown || [];
  const trend = overview.inspectionTrend || [];
  const userActivities = (overview.userActivity || []).map((item) => ({
    ...item,
    date: item.date ? new Date(item.date.replace(" ", "T")) : new Date(),
  }));
  const monitoringErrors = dashboardQuery.isError
    ? [
        {
          time: "Now",
          source: "Overview API",
          message: "Live data could not be loaded.",
          severity: "Error",
        },
      ]
    : overview.monitoringErrors || [];
  const attentionItems = [
    ...(categoryA > 0
      ? [
          {
            label: "Category A faults",
            value: categoryA,
            detail: "Immediate action required",
            color: COLORS.error,
            path: "/issue/",
          },
        ]
      : []),
    ...(categoryB > 0
      ? [
          {
            label: "Workshop repairs",
            value: categoryB,
            detail: "Repair required",
            color: COLORS.warning,
            path: "/issue/",
          },
        ]
      : []),
    ...(serviceDueCount > 0
      ? [
          {
            label: "Service interval reached",
            value: serviceDueCount,
            detail: "Review asset service",
            color: "#B45309",
            path: "/issue/",
          },
        ]
      : []),
    ...(openIssues > 0
      ? [
          {
            label: "Open inspection issues",
            value: openIssues,
            detail: "Review unresolved issues",
            color: COLORS.error,
            path: "/issue/",
          },
        ]
      : []),
  ];
  const invalidCustomRange =
    period === "Custom Date" &&
    customStart &&
    customEnd &&
    customStart > customEnd;

  /* Loading state */
  if (dashboardQuery.isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
        }}
      >
        <Skeleton
          variant="rounded"
          height={72}
          sx={{ mb: 3, borderRadius: 2 }}
          animation="wave"
        />
        <Grid container spacing={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={120}
                sx={{ borderRadius: 2.5 }}
                animation="wave"
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton
          variant="rounded"
          height={320}
          sx={{ borderRadius: 2.5 }}
          animation="wave"
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
       
        minHeight: "100vh",
      }}
    >
      {/* Preview banner */}
      {!hasLiveData && (
        <Alert
          severity={dashboardQuery.isError ? "warning" : "info"}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: `1px solid ${COLORS.border}`,
            alignItems: "center",
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => dashboardQuery.refetch()}
              sx={{ fontWeight: 600 }}
            >
              Retry live data
            </Button>
          }
        >
          Preview data is displayed because live overview data is not available
          yet.
        </Alert>
      )}

      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        mb={3.5}
        sx={{
          borderLeft: `4px solid ${COLORS.primary}`,
          pl: { xs: 1.75, md: 2.25 },
          py: 0.5,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={750}
            sx={{
              color: COLORS.textPrimary,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
            }}
          >
            Overview
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: COLORS.textSecondary, mt: 0.5 }}
          >
            Operational fleet health and inspection activity
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems="stretch"
        >
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="overview-period-label">Date range</InputLabel>
            <Select
              labelId="overview-period-label"
              value={period}
              label="Date range"
              onChange={(event) => setPeriod(event.target.value)}
              sx={{
                borderRadius: 1.5,
                backgroundColor: COLORS.surface,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.border,
                },
              }}
            >
              {PERIODS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => dashboardQuery.refetch()}
            disabled={dashboardQuery.isFetching}
            sx={{
              borderRadius: 1.5,
              borderColor: COLORS.border,
              color: COLORS.textPrimary,
              fontWeight: 600,
              px: 2,
              backgroundColor: COLORS.surface,
              "&:hover": {
                borderColor: COLORS.primaryLight,
                backgroundColor: alpha(COLORS.primary, 0.04),
              },
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Custom date range */}
      {period === "Custom Date" && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          mb={2.5}
          sx={{ maxWidth: 420 }}
        >
          <TextField
            size="small"
            type="date"
            label="Start date"
            value={customStart}
            onChange={(event) => setCustomStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: COLORS.surface,
              },
            }}
          />
          <TextField
            size="small"
            type="date"
            label="End date"
            value={customEnd}
            onChange={(event) => setCustomEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(invalidCustomRange)}
            helperText={
              invalidCustomRange
                ? "End date must be on or after start date."
                : ""
            }
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: COLORS.surface,
              },
            }}
          />
        </Stack>
      )}

      {dashboardQuery.isFetching && (
        <LinearProgress
          sx={{
            mb: 2.5,
            borderRadius: 1,
            height: 3,
            backgroundColor: alpha(COLORS.primary, 0.08),
            "& .MuiLinearProgress-bar": {
              backgroundColor: COLORS.primaryLight,
            },
          }}
        />
      )}

      {invalidCustomRange && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          Choose a valid date range to update period metrics.
        </Alert>
      )}

      {/* ========== Executive Summary ========== */}
      <SectionHeading
        eyebrow="Executive summary"
        title="Fleet at a glance"
        description="The current fleet position and activity for the selected period."
      />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total assets"
            value={totalAssets}
            hint={hasLiveData ? "Current fleet" : "Preview fleet"}
            icon={DirectionsCarIcon}
            onClick={() => navigate("/assets")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Inspections"
            value={invalidCustomRange ? "—" : periodInspections}
            hint={
              period === "Custom Date"
                ? overview.range?.start && overview.range?.end
                  ? `${overview.range.start} to ${overview.range.end}`
                  : "Custom range"
                : period
            }
            icon={AssignmentTurnedInIcon}
            color={COLORS.primaryLight}
            onClick={() => navigate("/prestart/")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Pre-start completion"
            value={`${completionRate}%`}
            hint={`${completed} completed of ${periodInspections}`}
            color={completionRate >= 80 ? COLORS.success : COLORS.warning}
            icon={CheckCircleOutlineIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Assets with issues"
            value={affectedAssets}
            hint={`${periodIssues} issues found`}
            color={affectedAssets ? COLORS.error : COLORS.success}
            icon={ReportProblemIcon}
          />
        </Grid>
      </Grid>

      {/* Service due alert */}
      {serviceDueCount > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mt: 2.5,
            mb: 1,
            borderRadius: 2,
            border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
            alignItems: "center",
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/issue/")}
              sx={{ fontWeight: 600 }}
            >
              View service
            </Button>
          }
        >
          <strong>{serviceDueCount}</strong> asset
          {serviceDueCount === 1 ? "" : "s"} have reached the configured
          service interval.
        </Alert>
      )}

      {/* ========== Needs Attention ========== */}
      <SectionHeading
        eyebrow="Risk and action"
        title="Needs attention"
        description="Priorities that may require operational follow-up."
      />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} md={7}>
          <SectionCard
            title="Needs attention"
            action={
              <Chip
                size="small"
                label={
                  attentionItems.length
                    ? `${attentionItems.length} priorit${attentionItems.length === 1 ? "y" : "ies"}`
                    : "Clear"
                }
                color={attentionItems.length ? "warning" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            }
          >
            {attentionItems.length ? (
              <Stack
                divider={
                  <Box sx={{ borderBottom: `1px solid ${COLORS.border}` }} />
                }
              >
                {attentionItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    sx={{
                      py: 1.5,
                      px: 0.5,
                      color: COLORS.textPrimary,
                      justifyContent: "space-between",
                      textTransform: "none",
                      textAlign: "left",
                      borderRadius: 1.5,
                      "&:hover": {
                        backgroundColor: alpha(item.color, 0.04),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: COLORS.textSecondary }}
                        >
                          {item.detail}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: item.color, ml: 2 }}
                    >
                      {item.value}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                  color: COLORS.success,
                  minHeight: 100,
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlineIcon />
                <Typography variant="body2" fontWeight={500}>
                  No immediate action is required.
                </Typography>
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Staff activity">
            <Stack spacing={2}>
              {[
                {
                  label: "Staff accounts",
                  value: totalUsers,
                  color: COLORS.textPrimary,
                },
                {
                  label: "Active staff",
                  value: activeUsers || "—",
                  color: COLORS.success,
                },
                {
                  label: "Inspection operators",
                  value: inspectionOperators || "—",
                  color: COLORS.textPrimary,
                },
              ].map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.textSecondary }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: row.color, letterSpacing: "-0.02em" }}
                  >
                    {row.value}
                  </Typography>
                </Stack>
              ))}
              <Typography
                variant="caption"
                sx={{ color: COLORS.muted, pt: 0.5 }}
              >
                Activity shown for the selected date range.
              </Typography>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ========== Monitoring ========== */}
      <SectionHeading
        eyebrow="System monitoring"
        title="Monitoring and alerts"
        description="Data connection status, recent errors, and service interval alerts."
      />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} md={4}>
          <SectionCard
            title="Monitoring status"
            action={
              <Chip
                size="small"
                label={dashboardQuery.isError ? "Degraded" : "Operational"}
                color={dashboardQuery.isError ? "warning" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            }
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CheckCircleOutlineIcon
                  color={dashboardQuery.isError ? "disabled" : "success"}
                  fontSize="small"
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Overview data service
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {dashboardQuery.isError ? "Review" : "Connected"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <NotificationsNoneIcon
                  color={intervalAlerts.length ? "warning" : "disabled"}
                  fontSize="small"
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Active alerts
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {intervalAlerts.length}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                Status reflects the latest Overview refresh.
              </Typography>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard
            title="Error log"
            action={
              <Chip
                size="small"
                label={
                  monitoringErrors.length
                    ? `${monitoringErrors.length} item${monitoringErrors.length === 1 ? "" : "s"}`
                    : "Clear"
                }
                color={monitoringErrors.length ? "warning" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            }
          >
            {monitoringErrors.length ? (
              <Stack spacing={1.5}>
                {monitoringErrors.slice(0, 3).map((item, index) => (
                  <Stack
                    key={`${item.source}-${index}`}
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                  >
                    <ErrorOutlineIcon
                      fontSize="small"
                      color={item.severity === "Error" ? "error" : "warning"}
                      sx={{ mt: 0.25 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {item.source} · {item.time}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.textSecondary }}
                      >
                        {item.message}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <EmptyState>No current monitoring errors.</EmptyState>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard
            title="Alert queue"
            action={
              <Chip
                size="small"
                label={`${intervalAlerts.length} due`}
                color={intervalAlerts.length ? "warning" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            }
          >
            {intervalAlerts.length ? (
              <Stack spacing={1.25}>
                {intervalAlerts.slice(0, 3).map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => navigate("/assets")}
                    sx={{
                      p: 0.75,
                      justifyContent: "space-between",
                      textTransform: "none",
                      color: COLORS.textPrimary,
                      borderRadius: 1.5,
                      "&:hover": {
                        backgroundColor: alpha(COLORS.warning, 0.06),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberIcon
                        fontSize="small"
                        color={item.status === "Overdue" ? "error" : "warning"}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {item.id}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={
                        item.status === "Overdue"
                          ? "error.main"
                          : "warning.main"
                      }
                    >
                      {item.status}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            ) : (
              <EmptyState>No interval alerts.</EmptyState>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* ========== Asset Intervals ========== */}
      <SectionHeading
        eyebrow="Maintenance control"
        title="Asset intervals"
        description="Usage against each asset's configured service threshold."
      />
      <SectionCard
        title="Asset interval report"
        action={
          <Button
            size="small"
            onClick={() => navigate("/assets")}
            sx={{ fontWeight: 600, color: COLORS.primary }}
          >
            View assets
          </Button>
        }
        sx={{ mb: 1 }}
      >
        {intervalReport.length ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Asset",
                    "Type",
                    "Current SMR",
                    "Interval",
                    "Remaining",
                    "Status",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      align={
                        ["Current SMR", "Interval", "Remaining"].includes(h)
                          ? "right"
                          : "left"
                      }
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                        py: 1.25,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {intervalReport.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      {item.id}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>
                      {item.type}
                    </TableCell>
                    <TableCell align="right">
                      {item.current.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {item.interval.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {item.remaining.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={item.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState>No configured asset intervals are available.</EmptyState>
        )}
      </SectionCard>

      {/* ========== User Activity ========== */}
      <SectionHeading
        eyebrow="People and workflow"
        title="User activity"
        description="Recent inspections, issues, and service actions by staff."
      />
      <SectionCard
        title="Recent user activity"
        action={
          <Chip
            size="small"
            label={`${userActivities.length} recent`}
            sx={{
              fontWeight: 600,
              height: 24,
              backgroundColor: alpha(COLORS.primary, 0.08),
              color: COLORS.primary,
            }}
          />
        }
        sx={{ mb: 1 }}
      >
        {userActivities.length ? (
          <Stack
            divider={
              <Box sx={{ borderBottom: `1px solid ${COLORS.border}` }} />
            }
          >
            {userActivities.map((item, index) => (
              <Stack
                key={`${item.action}-${item.date.toISOString()}-${index}`}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ py: 1.5 }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {item.user}{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: COLORS.textSecondary, fontWeight: 400 }}
                    >
                      {item.action.toLowerCase()}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    {item.detail}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.muted,
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {item.date.toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <EmptyState>
            No staff activity was recorded during this period.
          </EmptyState>
        )}
      </SectionCard>

      {/* ========== Coverage Gaps ========== */}
      <SectionHeading
        eyebrow="Inspection coverage"
        title="Assets without pre-start"
        description="Assets with no recorded pre-start inspection in the selected date range."
      />
      <SectionCard
        title="Inspection coverage gaps"
        action={
          <Chip
            size="small"
            label={`${assetsWithoutPrestart.length} asset${assetsWithoutPrestart.length === 1 ? "" : "s"}`}
            color={assetsWithoutPrestart.length ? "warning" : "success"}
            sx={{ fontWeight: 600, height: 24 }}
          />
        }
        sx={{ mb: 1 }}
      >
        {assetsWithoutPrestart.length ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  {["Asset", "Type", "Department", "Status", "Action"].map(
                    (h) => (
                      <TableCell
                        key={h}
                        align={h === "Action" ? "right" : "left"}
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                          py: 1.25,
                        }}
                      >
                        {h}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {assetsWithoutPrestart.map((asset) => (
                  <TableRow
                    key={asset.assetId}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      {asset.assetId}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>
                      {asset.catName || "Unclassified"}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>
                      {asset.departName || "Unassigned"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={asset.statusName || "Unknown"}
                        variant="outlined"
                        sx={{ height: 24, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate("/assets")}
                        sx={{ fontWeight: 600, color: COLORS.primary }}
                      >
                        View asset
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState>
            All listed assets have a pre-start inspection in this period.
          </EmptyState>
        )}
      </SectionCard>

      {/* ========== Operational Breakdown ========== */}
      <SectionHeading
        eyebrow="Fleet and inspection intelligence"
        title="Operational breakdown"
        description="Fleet composition, inspection performance, issue volume, and department distribution."
      />
      <Grid container spacing={2}>
        {/* Fleet composition */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Fleet composition">
            {assetTypes.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={assetTypes}
                  layout="vertical"
                  margin={{ left: 8, right: 16, top: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke={COLORS.border}
                  />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill={COLORS.primary}
                    radius={[0, 4, 4, 0]}
                    barSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState>No asset type data is available.</EmptyState>
            )}
          </SectionCard>
        </Grid>

        {/* Asset status */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Asset status">
            {assetStatuses.length ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                spacing={2.5}
              >
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={assetStatuses}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={88}
                      paddingAngle={3}
                      stroke="none"
                    >
                      {assetStatuses.map((item, index) => (
                        <Cell
                          key={item.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Stack spacing={1.25} sx={{ minWidth: 140 }}>
                  {assetStatuses.map((item, index) => (
                    <Stack
                      key={item.name}
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor:
                            CHART_COLORS[index % CHART_COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">
                        {item.name}: <strong>{item.value}</strong>
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <EmptyState>No asset status data is available.</EmptyState>
            )}
          </SectionCard>
        </Grid>

        {/* Inspection activity */}
        <Grid item xs={12} md={7}>
          <SectionCard title="Inspection activity">
            {invalidCustomRange ? (
              <EmptyState>
                Choose a valid date range to view inspection activity.
              </EmptyState>
            ) : trend.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={trend}
                  margin={{ left: 0, right: 8, top: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={COLORS.border}
                  />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  />
                  <Bar
                    dataKey="count"
                    name="Inspections"
                    fill={COLORS.primary}
                    radius={[4, 4, 0, 0]}
                    barSize={28}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState>
                No inspections were recorded during this period.
              </EmptyState>
            )}
          </SectionCard>
        </Grid>

        {/* Inspection issues */}
        <Grid item xs={12} md={5}>
          <SectionCard
            title="Inspection issues"
            action={
              <Chip
                size="small"
                label={`${periodIssues} total`}
                color={periodIssues ? "error" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            }
          >
            <Stack spacing={2}>
              {[
                ["Category A", categoryA, COLORS.error],
                ["Category B", categoryB, COLORS.warning],
                ["Category C", categoryC, COLORS.accent],
              ].map(([label, value, color]) => (
                <Box key={label}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    mb={0.5}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {value}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={
                      periodIssues
                        ? (value / periodIssues) * 100
                        : 0
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha(color, 0.12),
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: color,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              ))}
              <Typography
                variant="caption"
                sx={{ color: COLORS.muted, pt: 0.5 }}
              >
                Affected assets are counted once, even when they have multiple
                issues.
              </Typography>
            </Stack>
          </SectionCard>
        </Grid>

        {/* Assets by department */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Assets by department">
            {departments.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                      }}
                    >
                      Department
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                      }}
                    >
                      Assets
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.map((item) => (
                    <TableRow
                      key={item.name}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ py: 1.35 }}>{item.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {item.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState>No department assignments are available.</EmptyState>
            )}
          </SectionCard>
        </Grid>

        {/* Operational context */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Operational context">
            <Stack spacing={2}>
              {[
                {
                  label: "Users",
                  value: totalUsers,
                  color: COLORS.textPrimary,
                },
                {
                  label: "Service interval due",
                  value: serviceDueCount,
                  color: serviceDueCount ? COLORS.error : COLORS.success,
                },
                {
                  label: "Assets without department",
                  value: overview.assetsWithoutDepartment ?? 0,
                  color: COLORS.textPrimary,
                },
              ].map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.textSecondary }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    fontWeight={700}
                    sx={{ color: row.color, fontSize: "1.1rem" }}
                  >
                    {row.value}
                  </Typography>
                </Stack>
              ))}
              <Typography
                variant="caption"
                sx={{ color: COLORS.muted, pt: 0.5 }}
              >
                Current-state figures are independent of the selected activity
                period.
              </Typography>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {!totalAssets && (
        <Alert
          severity="info"
          icon={<CheckCircleOutlineIcon />}
          sx={{ mt: 3, borderRadius: 2 }}
        >
          No assets are available for this account.
        </Alert>
      )}
    </Box>
  );
};

export default DashboardOverview;
